import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import axios from 'axios';
import { parseISO, isWithinInterval, isSameDay, addMonths, startOfMonth, endOfMonth, eachDayOfInterval, format, isBefore, startOfDay } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const Availability = () => {
  const { t, i18n } = useTranslation();
  const [unavailableDates, setUnavailableDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const maxMonthOffset = 6; // Limiter à 6 mois

  const canGoPrevious = () => {
    const today = new Date();
    return currentMonth > today;
  };

  const canGoNext = () => {
    const today = new Date();
    const maxDate = addMonths(today, maxMonthOffset - 1);
    return currentMonth < maxDate;
  };

  const handlePreviousMonth = () => {
    if (canGoPrevious()) {
      setCurrentMonth(addMonths(currentMonth, -1));
    }
  };

  const handleNextMonth = () => {
    if (canGoNext()) {
      setCurrentMonth(addMonths(currentMonth, 1));
    }
  };

  useEffect(() => {
    fetchAvailability();
  }, []);

  const fetchAvailability = async () => {
    try {
      setLoading(true);
      const cacheBuster = Date.now();
      const response = await axios.get(`${API}/availability?_=${cacheBuster}`);
      setUnavailableDates(response.data);
    } catch (error) {
      console.error('Error fetching availability:', error);
      toast.error(t('availability.error'));
    } finally {
      setLoading(false);
    }
  };

  const isDateUnavailable = (date) => {
    return unavailableDates.some((range) => {
      try {
        const startDate = parseISO(range.start);
        const endDate = parseISO(range.end);
        // Check if date is within range, but EXCLUDE the end date (checkout day is available)
        return isWithinInterval(date, { start: startDate, end: endDate }) && !isSameDay(date, endDate);
      } catch (e) {
        return false;
      }
    });
  };

  const handleWhatsAppBooking = () => {
    const lang = i18n.language;
    const message = lang === 'fr' 
      ? 'Bonjour, je souhaite réserver le Riad KAMA en exclusivité. Pouvez-vous me confirmer les disponibilités et tarifs ?'
      : 'Hello, I would like to book Riad KAMA exclusively. Could you confirm availability and rates?';
    
    const whatsappUrl = `https://wa.me/33670133353?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const renderCalendar = (monthOffset = 0) => {
    const month = addMonths(currentMonth, monthOffset);
    const start = startOfMonth(month);
    const end = endOfMonth(month);
    const days = eachDayOfInterval({ start, end });
    const locale = i18n.language === 'fr' ? fr : enUS;
    const today = startOfDay(new Date());

    const weekDays = ['Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa', 'Di'];
    const firstDay = start.getDay();
    const emptyDays = firstDay === 0 ? 6 : firstDay - 1;

    return (
      <div className="flex-1">
        <h3 className="text-2xl font-serif mb-6 text-[#B8860B] text-center" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
          {format(month, 'MMMM yyyy', { locale })}
        </h3>
        
        <div className="grid grid-cols-7 gap-2 mb-2">
          {weekDays.map((day) => (
            <div key={day} className="text-center text-xs uppercase tracking-wider text-[#1A1A1A]/60 py-2 font-medium">
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-2">
          {Array.from({ length: emptyDays }).map((_, i) => (
            <div key={`empty-${i}`}></div>
          ))}
          {days.map((day) => {
            const isPast = isBefore(day, today);
            const unavailable = isDateUnavailable(day);
            const isToday = isSameDay(day, today);
            
            return (
              <div
                key={day.toISOString()}
                className={`calendar-day ${
                  isPast ? 'past' : unavailable ? 'unavailable' : 'available'
                } ${isToday ? 'today' : ''}`}
              >
                {format(day, 'd')}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div data-testid="availability-page" className="bg-[#FAF8F5] min-h-screen">
      <section className="pt-48 pb-24 px-8 md:px-16 lg:px-32">
        <div className="max-w-6xl mx-auto">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-[#B8860B] text-xs uppercase tracking-[0.3em] mb-6 text-center font-medium" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            {t('availability.contact')}
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="font-serif text-5xl md:text-7xl text-[#1A1A1A] mb-6 text-center" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: '300' }}>
            {t('availability.title')}
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="text-lg text-[#1A1A1A]/80 max-w-3xl mx-auto text-center leading-relaxed mb-8" style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: '400' }}>
            {t('availability.intro')}
          </motion.p>
          <motion.p initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="text-base text-[#B8860B] text-center mb-16 font-medium" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            {t('availability.capacity')}
          </motion.p>
        </div>
      </section>

      <section className="pb-32 px-8 md:px-16 lg:px-32">
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <div className="text-center py-24">
              <div className="inline-block w-12 h-12 border-4 border-[#B8860B] border-t-transparent rounded-full animate-spin"></div>
              <p className="mt-6 text-lg text-[#1A1A1A]/70" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                {t('availability.loading')}
              </p>
            </div>
          ) : (
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="bg-white p-12 md:p-16 shadow-xl border border-[#D4AF37]/20">
              <div className="flex items-center justify-between mb-8">
                <button
                  onClick={handlePreviousMonth}
                  disabled={!canGoPrevious()}
                  className="p-3 hover:bg-[#D4AF37]/10 transition-colors border border-[#B8860B]/40 disabled:opacity-30 disabled:cursor-not-allowed"
                  aria-label="Mois précédent"
                >
                  <ChevronLeft size={24} className="text-[#B8860B]" />
                </button>
                
                <h3 className="text-2xl font-serif text-[#B8860B]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  {format(currentMonth, 'MMMM yyyy', { locale: i18n.language === 'fr' ? fr : enUS })}
                </h3>
                
                <button
                  onClick={handleNextMonth}
                  disabled={!canGoNext()}
                  className="p-3 hover:bg-[#D4AF37]/10 transition-colors border border-[#B8860B]/40 disabled:opacity-30 disabled:cursor-not-allowed"
                  aria-label="Mois suivant"
                >
                  <ChevronRight size={24} className="text-[#B8860B]" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-12">
                {renderCalendar(0)}
                {renderCalendar(1)}
              </div>

              <div className="flex gap-8 justify-center text-sm mb-12 pb-8 border-b border-[#D4AF37]/30" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded bg-green-500/20 border-2 border-green-600"></div>
                  <span className="text-[#1A1A1A]/80 font-medium">{t('availability.available')}</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded bg-red-500/20 border-2 border-red-600"></div>
                  <span className="text-[#1A1A1A]/80 font-medium">{t('availability.reserved')}</span>
                </div>
              </div>

              <div className="text-center">
                <p className="text-lg text-[#1A1A1A]/80 mb-8" style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: '400' }}>
                  {t('availability.whatsappText')}
                </p>
                <button onClick={handleWhatsAppBooking} data-testid="whatsapp-booking-button" className="gold-gradient text-[#0A0A0A] px-16 py-6 hover:shadow-2xl transition-all duration-700 uppercase tracking-[0.2em] text-xs font-semibold group inline-flex items-center justify-center gap-3" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  {t('availability.whatsappButton')}
                  <ArrowRight size={18} className="group-hover:translate-x-2 transition-transform duration-500" />
                </button>
              </div>
            </motion.div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Availability;
